import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Pencil } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/data-table";
import type { Role } from "@/lib/api/admin-api";

/**
 * The roles list, shared by the super-admin `/rolesperms` page and the
 * org-level `/roles` page. Those two carried near-identical hand-written
 * `<table>` markup; the only real difference is which columns they show, so
 * that is the prop.
 */

export type RoleColumnId =
  | "name"
  | "code"
  | "description"
  | "permissions"
  | "type"
  | "actions";

const ALL_COLUMNS: RoleColumnId[] = [
  "name",
  "code",
  "description",
  "permissions",
  "type",
  "actions",
];

/** `*:*` is stored as a single wildcard entry but means "everything". */
function permissionCount(role: Role): string[] {
  return role.permissions as string[];
}

export interface RolesTableProps {
  roles: Role[];
  /** Opens the role for editing. Not called for protected rows. */
  onSelect: (role: Role) => void;
  /**
   * Rows that cannot be edited — they render a "Protected" label instead of the
   * edit button and ignore row clicks. Defaults to none.
   */
  isProtected?: (role: Role) => boolean;
  /** Defaults to every column. */
  columns?: RoleColumnId[];
  emptyMessage?: string;
  className?: string;
}

export function RolesTable({
  roles,
  onSelect,
  isProtected = () => false,
  columns = ALL_COLUMNS,
  emptyMessage,
  className,
}: RolesTableProps) {
  const { t } = useLingui();

  const columnDefs = useMemo<ColumnDef<Role>[]>(() => {
    const byId: Record<RoleColumnId, ColumnDef<Role>> = {
      name: {
        id: "name",
        header: () => t`Name`,
        meta: { label: t`Name` },
        cell: ({ row }) => (
          <span
            className={
              isProtected(row.original)
                ? "font-medium"
                : "font-medium underline-offset-2 hover:underline"
            }
          >
            {row.original.name}
          </span>
        ),
      },
      code: {
        id: "code",
        header: () => t`Code`,
        meta: { label: t`Code` },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-(--color-text-secondary)">
            {row.original.code}
          </span>
        ),
      },
      description: {
        id: "description",
        header: () => t`Description`,
        meta: { label: t`Description`, className: "hidden md:table-cell" },
        cell: ({ row }) => (
          <span className="block max-w-70 truncate text-(--color-text-secondary)">
            {row.original.description ?? (
              <span className="text-(--gray-300)">-</span>
            )}
          </span>
        ),
      },
      permissions: {
        id: "permissions",
        header: () => t`Permissions`,
        meta: { label: t`Permissions`, className: "text-center" },
        cell: ({ row }) => {
          const permissions = permissionCount(row.original);

          return (
            <Badge variant="neutral">
              {permissions.join("") === "*:*" ? t`All` : permissions.length}
            </Badge>
          );
        },
      },
      type: {
        id: "type",
        header: () => t`Type`,
        meta: { label: t`Type`, className: "text-center" },
        cell: ({ row }) =>
          row.original.isSystem ? (
            <Badge variant="neutral">{t`System`}</Badge>
          ) : (
            <Badge>{t`Custom`}</Badge>
          ),
      },
      actions: {
        id: "actions",
        header: () => t`Actions`,
        meta: { label: t`Actions`, className: "text-right" },
        cell: ({ row }) =>
          isProtected(row.original) ? (
            <span className="px-2 text-xs text-(--color-text-secondary)">
              {t`Protected`}
            </span>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() => onSelect(row.original)}
              aria-label={t`Edit role`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ),
      },
    };

    return columns.map((id) => byId[id]);
  }, [columns, isProtected, onSelect, t]);

  return (
    <DataTable
      columns={columnDefs}
      data={roles}
      className={className}
      emptyMessage={emptyMessage ?? t`No roles found`}
      onRowClick={(role) => {
        if (!isProtected(role)) onSelect(role);
      }}
    />
  );
}
